import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { InputComponent } from 'src/app/shared/components/ui/input/input.component';
import { ContentService } from 'src/app/views/content/content.service';

@Component({
  selector: 'app-course-selection-dialog',
  templateUrl: './course-selection-dialog.component.html',
  styleUrls: ['./course-selection-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    TranslateModule,
    InputComponent,
  ],
})
export class CourseSelectionDialogComponent implements OnInit, OnDestroy {
  dialogRef = inject(MatDialogRef<CourseSelectionDialogComponent>);
  private contentService = inject(ContentService);
  private translate = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);

  courses: any[] = [];
  filteredCourses: any[] = [];
  loading = false;
  searchInput = '';

  private langSub: Subscription;

  ngOnInit(): void {
    this.getCourses();
    this.langSub = this.translate.onLangChange.subscribe(() => {
      this.mapCourseNames();
      this.filterCourses();
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  getCourses(): void {
    this.loading = true;
    this.contentService.getCourses(0, 1000, 'all').then((sub) => {
      sub.subscribe({
        next: (res: any) => {
          this.courses = res.courses || [];
          this.mapCourseNames();
          this.filterCourses();
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error(err);
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
    });
  }

  mapCourseNames(): void {
    if (!this.courses) return;
    this.courses = this.courses.map((course) => {
      if (course.__name?.editions) {
        const edition = course.__name.editions.find((e: any) => e.language === this.translate.currentLang);
        course.actualName = edition ? edition.content : course.__name.editions[0]?.content;
      } else {
        course.actualName = course.name || 'Untitled Course';
      }
      course.sectionCount = (course.sections || []).length;
      const standaloneLectures = (course.lectures || []).length;
      const sectionLectures = (course.sections || []).reduce((acc: number, s: any) => acc + (s.lectures?.length || 0), 0);
      course.lectureCount = standaloneLectures + sectionLectures;
      return course;
    });
  }

  filterCourses(): void {
    if (!this.searchInput) {
      this.filteredCourses = [...this.courses];
      return;
    }
    const lowerSearch = this.searchInput.toLowerCase();
    this.filteredCourses = this.courses.filter((course) => course.actualName?.toLowerCase().includes(lowerSearch));
  }

  selectCourse(course: any): void {
    this.dialogRef.close(course);
  }
}
